import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import { 
  Send, 
  MessageSquare, 
  User, 
  Trash2, 
  ArrowLeft,
  Sparkles,
  Lock,
  Crown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/doslions-logo.png";
const getToken = () => localStorage.getItem("token");

export default function Chat() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [lionaiPower, setLionaiPower] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Fetch LIONAI Power status
  const fetchLionaiPower = async () => {
    try {
      const response = await axios.get(`${API}/lionai/power`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setLionaiPower(response.data);
    } catch (error) {
      console.error("Failed to load LIONAI Power status");
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!user?.is_subscribed) {
      setHistoryLoading(false);
      return;
    }
    
    // Fetch LIONAI Power on mount
    fetchLionaiPower();
    
    const fetchHistory = async () => {
      try {
        const response = await axios.get(`${API}/chat/history`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        const history = response.data.map(chat => ([
          { type: 'user', text: chat.user_message, timestamp: chat.timestamp },
          { type: 'ai', text: chat.ai_response, timestamp: chat.timestamp }
        ])).flat().reverse();
        setMessages(history);
      } catch (error) {
        console.error("Failed to load chat history");
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();

    const query = searchParams.get("query");
    if (query) {
      setInput(query);
    }
  }, [searchParams, user?.is_subscribed]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    if (!user?.is_subscribed) {
      toast.error("Subscribe to Pro to use AI Chat");
      navigate("/subscription");
      return;
    }

    const userMessage = { type: 'user', text: input.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat`,
        { message: userMessage.text },
        { headers: { Authorization: `Bearer ${getToken()}` }}
      );
      
      const aiMessage = { 
        type: 'ai', 
        text: response.data.ai_response, 
        timestamp: response.data.timestamp 
      };
      setMessages(prev => [...prev, aiMessage]);
      
      // Refresh LIONAI Power after successful message
      fetchLionaiPower();
    } catch (error) {
      // Handle out of power
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || "";
        if (detail.includes("Power") || detail.includes("Recharge")) {
          toast.error("Out of LIONAI Power! Recharge to continue your market research.");
        } else {
          toast.error(detail || "Access denied");
        }
      } else {
        toast.error("Failed to get AI response");
      }
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([]);
    toast.success("Chat cleared");
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Show subscription gate if not subscribed
  if (!user?.is_subscribed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-amber-500/10 mx-auto flex items-center justify-center mb-6">
            <Lock className="w-10 h-10 text-amber-500" />
          </div>
          <h1 
            className="text-4xl font-bold tracking-tight uppercase mb-4"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            LIONAI <span className="text-amber-500">Locked</span>
          </h1>
          <p className="text-zinc-400 mb-4">
            LIONAI Chat is a <span className="text-amber-500 font-bold">Pro Ecopreneur</span> feature. 
            Upgrade to get 10 AI-powered market insights per day!
          </p>
          <p className="text-sm text-zinc-500 mb-8">
            Ask about stocks, commodities, crypto, and get personalized market analysis.
          </p>
          <div className="space-y-4">
            <Button 
              onClick={() => navigate("/subscription")}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider py-6 shadow-[0_0_20px_rgba(245,158,11,0.3)]"
              data-testid="unlock-ai-btn"
            >
              <Crown className="w-5 h-5 mr-2" />
              Upgrade to Pro Ecopreneur - $4.99
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="w-full border-zinc-700 text-zinc-400"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Markets
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#0A0A0A] flex flex-col">
      {/* Header */}
      <header className="flex-shrink-0 bg-black/80 backdrop-blur-xl border-b border-amber-500/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="text-zinc-400 hover:text-amber-500"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div 
                className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate("/dashboard")}
                data-testid="logo-home-link"
              >
                <div className="w-8 h-8 bg-amber-500/20 flex items-center justify-center">
                  <img src={LOGO_URL} alt="LIONAI" className="w-5 h-5 object-contain" />
                </div>
                <div>
                  <p 
                    className="font-bold tracking-tight text-amber-500"
                    style={{ fontFamily: 'Barlow Condensed' }}
                  >
                    LIONAI
                  </p>
                  <p className="text-xs text-zinc-500">Market Analyst</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* LIONAI Power Indicator */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/30">
                <Crown className="w-3 h-3 text-amber-500" />
                <span className="text-xs text-amber-500 font-bold">
                  Power: {lionaiPower?.power_percent || 0}% 
                  <span className="text-zinc-500 ml-1">({lionaiPower?.power_remaining || 0} left)</span>
                </span>
                <button 
                  onClick={() => navigate("/dashboard?tab=trending")}
                  className="text-purple-400 hover:text-purple-300 text-xs ml-1"
                >
                  Recharge
                </button>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={clearChat}
                className="text-zinc-400 hover:text-amber-500"
                data-testid="clear-chat-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Chat Messages */}
      <ScrollArea className="flex-1 px-4">
        <div className="max-w-3xl mx-auto py-6 space-y-6">
          {historyLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-pulse text-zinc-500">Loading chat history...</div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-20 h-20 bg-amber-500/10 flex items-center justify-center mb-6">
                <MessageSquare className="w-10 h-10 text-amber-500" />
              </div>
              <h2 
                className="text-3xl font-bold tracking-tight uppercase mb-3"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                Ask <span className="text-amber-500">LIONAI</span> Anything
              </h2>
              <p className="text-lg text-zinc-400 max-w-md mb-10">
                Get time-saving insights about stocks, commodities, market trends, and project research data.
              </p>
              
              {/* Guided Questions Section */}
              <div className="w-full max-w-3xl mb-8">
                <p className="text-sm text-amber-500 uppercase tracking-wider mb-6 font-bold">How can LIONAI help you today?</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Project Research */}
                  <div className="bg-[#111111] border border-zinc-800 p-6 hover:border-amber-500/30 transition-colors">
                    <p className="text-amber-500 font-bold text-base mb-4">📊 Project Research</p>
                    <div className="space-y-3">
                      {[
                        "Analyze AAPL stock data for my project",
                        "What's the trend for Bitcoin this month?",
                        "Compare gold vs silver performance"
                      ].map((q) => (
                        <button 
                          key={q}
                          onClick={() => setInput(q)}
                          className="w-full text-left text-sm text-zinc-400 hover:text-white p-3 bg-zinc-900/50 hover:bg-zinc-800 transition-colors"
                        >
                          → {q}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Market Overview */}
                  <div className="bg-[#111111] border border-zinc-800 p-6 hover:border-emerald-500/30 transition-colors">
                    <p className="text-emerald-500 font-bold text-base mb-4">🌍 Market Overview</p>
                    <div className="space-y-3">
                      {[
                        "What are the top performing stocks today?",
                        "Give me a crypto market summary",
                        "How are commodities performing?"
                      ].map((q) => (
                        <button 
                          key={q}
                          onClick={() => setInput(q)}
                          className="w-full text-left text-sm text-zinc-400 hover:text-white p-3 bg-zinc-900/50 hover:bg-zinc-800 transition-colors"
                        >
                          → {q}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Time Saving */}
                  <div className="bg-[#111111] border border-zinc-800 p-4 hover:border-blue-500/30 transition-colors">
                    <p className="text-blue-500 font-bold text-sm mb-2">⏱️ Save Time</p>
                    <div className="space-y-2">
                      {[
                        "Summarize NVDA's recent performance",
                        "Quick overview of oil prices",
                        "What should I track this week?"
                      ].map((q) => (
                        <button 
                          key={q}
                          onClick={() => setInput(q)}
                          className="w-full text-left text-xs text-zinc-400 hover:text-white p-2 bg-zinc-900/50 hover:bg-zinc-800 transition-colors"
                        >
                          → {q}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Ideas & Learning */}
                  <div className="bg-[#111111] border border-zinc-800 p-4 hover:border-purple-500/30 transition-colors">
                    <p className="text-purple-500 font-bold text-sm mb-2">💡 Ideas & Learning</p>
                    <div className="space-y-2">
                      {[
                        "Explain what affects gold prices",
                        "How do I start tracking crypto?",
                        "What data should beginners watch?"
                      ].map((q) => (
                        <button 
                          key={q}
                          onClick={() => setInput(q)}
                          className="w-full text-left text-xs text-zinc-400 hover:text-white p-2 bg-zinc-900/50 hover:bg-zinc-800 transition-colors"
                        >
                          → {q}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-zinc-600 mb-4">Or type your own question below</p>
            </div>
          ) : (
            messages.map((message, index) => (
              <div 
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
              >
                <div className={`max-w-[85%] ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
                  <div className={`flex items-start gap-4 ${message.type === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center ${
                      message.type === 'user' 
                        ? 'bg-zinc-800' 
                        : 'bg-amber-500/20'
                    }`}>
                      {message.type === 'user' 
                        ? <User className="w-5 h-5 text-zinc-400" />
                        : <img src={LOGO_URL} alt="LIONAI" className="w-6 h-6 object-contain" />
                      }
                    </div>
                    <div className={`${
                      message.type === 'user' 
                        ? 'user-bubble' 
                        : 'ai-bubble'
                    } px-5 py-4`}>
                      <p className="text-base whitespace-pre-wrap leading-relaxed">
                        {message.text}
                      </p>
                      <p className="text-sm text-zinc-500 mt-3">
                        {formatTime(message.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
          
          {loading && (
            <div className="flex justify-start animate-fade-in">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-500/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-amber-500 animate-pulse" />
                </div>
                <div className="ai-bubble px-5 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="flex-shrink-0 border-t border-amber-500/20 bg-[#111111]">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-end gap-3">
            <div className="flex-1 relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about stocks, commodities, markets..."
                className="w-full terminal-input resize-none min-h-[48px] max-h-[200px] pr-4"
                rows={1}
                disabled={loading}
                data-testid="chat-input"
              />
            </div>
            <Button 
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className="bg-amber-500 hover:bg-amber-600 text-black h-12 w-12 p-0"
              data-testid="send-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-xs text-zinc-600 mt-3 text-center">
            LIONAI provides analysis, not financial advice. Each message uses 1 LIONAI Power. Need more? Check out our <button onClick={() => navigate("/#ai-builder")} className="text-amber-500 hover:underline">AI Builder services</button>.
          </p>
        </div>
      </div>
    </div>
  );
}
